import { useCountByStatus } from "@/api/bibliography/bibliography";
import { PagingParameters } from "@/api/dtos";
import {
  useDeleteComplexLink,
  useGetComplexRelationFields,
  useGetNotRelatedResource,
} from "@/api/related-fields";
import { FieldConfig } from "@/config/forms/general";
import {
  Author,
  Bibliography,
  Document,
  DocumentInBibliography,
} from "@/lib/types";
import { ColumnDef, Row } from "@tanstack/react-table";
import { ChevronLeft, PlusIcon, Trash2Icon } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ShowElements from "../drawer/ShowElements";
import { LinkableType } from "../LinksManagement";
import { HtmlCell, ImageCell, TextCell } from "../table/CustomCell";
import { Button } from "../ui/button";
import { DataTable } from "../ui/datatable";
import { Input } from "../ui/input";
import { LinksTableProps } from "./ArtworkInAuction";
import DocumentInBibliographyDrawer from "./drawer/DocumentInBibliographyDrawer";

interface ComplexLinkProps {
  id: string;
  document: Document | undefined;
  bibliography: Bibliography | undefined;
  fields: FieldConfig[] | undefined;
  type: LinkableType;
}

const columnsForBibliography = [
  {
    accessorKey: "bibliography.bibliographieDescriptionNumeroBibliographie",
    header: "bibliographieDescriptionNumeroBibliographie",
    size: 100,
    cell: (props: any) => {
      return <TextCell value={props.getValue()} />;
    },
  },
  {
    accessorKey: "vignette",
    header: "",
    size: 100,
    cell: ({ row }: { row: Row<DocumentInBibliography> }) => {
      return (
        <ImageCell
          value={""}
          attachments={row.original.attachments ?? []}
          domain={"bibliography"}
        />
      );
    },
  },
  {
    accessorKey: "bibliography.bibliographieDescriptionAnnee",
    header: "bibliographieDescriptionAnnee",
    size: 50,
    cell: (props: any) => {
      return <TextCell value={props.getValue()} />;
    },
  },
];

const documentColumns = [
  {
    accessorKey: "document.documentsDescriptionNumeroEnregistrement",
    header: "N°",
    size: 100,
    cell: (props: any) => {
      return <TextCell value={props.getValue()} />;
    },
  },
  {
    accessorKey: "vignette",
    header: "",
    size: 100,
    cell: ({ row }: { row: Row<DocumentInBibliography> }) => {
      return (
        <ImageCell
          value={""}
          attachments={row.original.attachments ?? []}
          domain={"document"}
        />
      );
    },
  },
  {
    accessorKey: "document.documentsDescriptionTitre",
    header: "Titre",
    size: 300,
    cell: (props: any) => {
      return <HtmlCell value={props.getValue()} />;
    },
  },
  {
    accessorKey: "document.documentsDescriptionType",
    header: "Type",
    size: 100,
    cell: (props: any) => {
      return <TextCell value={props.getValue()} />;
    },
  },
];

/**
 * DocumentInBibliographyLinks component
 * @returns {JSX.Element}
 */
const DocumentInBibliographyLinks = ({
  id,
  document,
  fields,
  type,
  bibliography,
}: ComplexLinkProps) => {
  const { t } = useTranslation();
  const { data: relatedData } = useGetComplexRelationFields(
    id,
    "documentInBibliography",
    type,
  );

  const { mutate: deleteDocumentInBibliography } = useDeleteComplexLink(
    id,
    "documentInBibliography",
    type,
  );

  const [searchContent, setSearchContent] = useState("");

  const [isAddMode, setIsAddMode] = useState(false);
  const handleDeleteRelation = useCallback(
    (id: string) => {
      deleteDocumentInBibliography({
        id,
        relation: "documentInBibliography",
      });
    },
    [deleteDocumentInBibliography],
  );

  const columns: ColumnDef<DocumentInBibliography, any>[] = useMemo(() => {
    return [
      ...(type === "document"
        ? columnsForBibliography.map((column) => ({
            ...column,
            header: t(column.header),
          }))
        : documentColumns.map((column) => ({
            ...column,
            header: t(column.header),
          }))),
      {
        accessorKey: "documentsDansBibliographieDescriptionReproduitPage",
        header: t("documentsDansBibliographieDescriptionReproduitPage"),
      },
      {
        accessorKey: "documentsDansBibliographieDescriptionCitePage",
        header: t("documentsDansBibliographieDescriptionCitePage"),
      },
      {
        accessorKey: "documentsDansBibliographieDescriptionReproductionCouleur",
        header: t("documentsDansBibliographieDescriptionReproductionCouleur"),
        size: 50,
        cell: (props: any) => {
          return <TextCell value={props.getValue() ? "Oui" : "Non"} />;
        },
      },
      {
        accessorKey: "documentsDansBibliographieDescriptionNonPagine",
        header: t("documentsDansBibliographieDescriptionNonPagine"),
        size: 50,
        cell: (props: any) => {
          return <TextCell value={props.getValue() ? "Oui" : "Non"} />;
        },
      },
      {
        accessorKey: "documentsDansBibliographieDescriptionPhotographe",
        header: t("documentsDansBibliographieDescriptionPhotographe"),
      },
      {
        accessorKey: "actions",
        header: "",
        size: 100,
        cell: ({ row }) => {
          const data = row.original as DocumentInBibliography;
          return (
            <div className="flex items-center gap-1 justify-end mr-2">
              <Button
                className="font-semibold flex gap-2 p-1"
                variant="ghost"
                type="button"
                onClick={() => handleDeleteRelation(data.id)}
              >
                <Trash2Icon className="w-5 h-5 cursor-pointer text-gray-500" />
              </Button>
              <DocumentInBibliographyDrawer
                key={row.original.id}
                fields={fields}
                queryId={id}
                type={type}
                data={data}
              />
              {type === "document" && (
                <ShowElements
                  type="bibliography"
                  id={row.original.bibliography.id}
                  elements={[
                    {
                      label: t("bibliographieDescriptionNumeroBibliographie"),
                      value:
                        row.original.bibliography
                          .bibliographieDescriptionNumeroBibliographie,
                      type: "text",
                    },
                    {
                      label: t("bibliographieDescriptionResumeLong"),
                      value:
                        row.original.bibliography
                          .bibliographieDescriptionResumeLong,
                      type: "richtext",
                    },
                    {
                      label: t("attachments"),
                      value: row.original.bibliography.attachments,
                      type: "image",
                    },
                  ]}
                />
              )}
              {type === "bibliography" && (
                <ShowElements
                  type="document"
                  id={row.original.document.id}
                  elements={[
                    {
                      label: t("documentsDescriptionNumeroEnregistrement"),
                      value:
                        row.original.document
                          .documentsDescriptionNumeroEnregistrement,
                      type: "text",
                    },
                    {
                      label: t("documentsDescriptionLegendeRedigee"),
                      value:
                        row.original.document
                          .documentsDescriptionLegendeRedigee,
                      type: "richtext",
                    },
                    {
                      label: t("attachments"),
                      value: row.original.document.attachments,
                      type: "image",
                    },
                  ]}
                />
              )}
            </div>
          );
        },
      },
    ];
  }, [fields, handleDeleteRelation, id, t, type]);

  const datatableConf = useMemo(() => {
    if (type === "document") {
      return (
        <DocumentToBibliographyLinks
          id={id}
          relatedData={relatedData}
          fields={fields}
          document={document}
          isAddMode={isAddMode}
          columnsLink={columns}
          searchContent={searchContent}
        />
      );
    } else {
      return (
        <BibliographyToDocumentLinks
          id={id}
          relatedData={relatedData}
          fields={fields}
          bibliography={bibliography}
          isAddMode={isAddMode}
          columnsLink={columns}
          searchContent={searchContent}
        />
      );
    }
  }, [
    type,
    id,
    relatedData,
    fields,
    document,
    isAddMode,
    columns,
    searchContent,
    bibliography,
  ]);

  return (
    <div className="w-full">
      {isAddMode && (
        <div
          className="flex items-center gap-1 mb-4 cursor-pointer"
          onClick={() => setIsAddMode(false)}
        >
          <ChevronLeft className="w-3 h-3 text-gray-300" />
          <p className="text-sm text-[#006EAB] hover:text-[#005D8D]">
            {t("linkedEntries")}
          </p>
        </div>
      )}
      <div className="flex justify-between items-center mb-2">
        <h2 className="text-lg font-semibold">
          {type == "document"
            ? t("linkedBibliographies")
            : t("linkedDocuments")}
        </h2>
        {!isAddMode && (
          <Button variant="outline" onClick={() => setIsAddMode(true)}>
            <PlusIcon className="w-4 h-4 mr-2" />
            {t("add")}
          </Button>
        )}
        {isAddMode && (
          <div className="flex items-center gap-2">
            <Input
              placeholder={t("search")}
              className="w-80"
              value={searchContent}
              onChange={(e) => setSearchContent(e.target.value)}
            />
          </div>
        )}
      </div>
      {datatableConf}
    </div>
  );
};

type DocumentToBibliographyLinksProps =
  LinksTableProps<DocumentInBibliography> & {
    document: Document | undefined;
    bibliography?: never;
  };

type BibliographyToDocumentLinksProps =
  LinksTableProps<DocumentInBibliography> & {
    bibliography: Bibliography | undefined;
    document?: never;
  };

//Composant pour Document
const DocumentToBibliographyLinks = ({
  id,
  relatedData,
  fields,
  document,
  isAddMode,
  columnsLink,
  searchContent,
}: DocumentToBibliographyLinksProps) => {
  const { t } = useTranslation();
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  const { data: notRelatedBibliographies } =
    useGetNotRelatedResource<Bibliography>({
      resultType: "bibliography",
      resourceId: id,
      pagination: getPagination,
      searchQuery: searchContent,
    });

  const columnsBibliographies: ColumnDef<Bibliography, any>[] = useMemo(() => {
    return [
      {
        accessorKey: "bibliographieDescriptionNumeroBibliographie",
        header: t("bibliographiesDescriptionNumeroBibliographie"),
        enableResizing: true,
        cell: ({ row }) => {
          return (
            <TextCell
              value={row.original.bibliographieDescriptionNumeroBibliographie}
            />
          );
        },
      },
      {
        accessorKey: "bibliographieDescriptionVignette",
        header: "",
        size: 100,
        cell: ({ row }) => {
          return (
            <ImageCell
              value={""}
              attachments={row.original.attachments}
              domain={"bibliography"}
            />
          );
        },
      },
      {
        accessorKey: "bibliographieDescriptionAnnee",
        header: t("bibliographiesDescriptionAnnee"),
        size: 100,
        cell: ({ row }) => {
          return (
            <TextCell value={row.original.bibliographieDescriptionAnnee} />
          );
        },
      },
      {
        accessorKey: "relatedAuthor",
        header: t("bibliographiesDescriptionAuteur"),
        size: 200,
        cell: ({ row }) => {
          return (
            <div className="flex items-center gap-3 text-wrap">
              <p className="text-[14px] font-[500]">
                {row.original.relatedAuthor
                  .map((author: Author) => author.artistesAuteursDescriptionNom)
                  .join(", ")}
              </p>
            </div>
          );
        },
      },
      {
        accessorKey: "actions",
        header: "",
        size: 100,
        cell: ({ row }) => {
          const data = row.original as Bibliography;
          if (!document) return null;
          return (
            <div className="flex items-center gap-1 justify-end mr-2">
              <ShowElements
                type="bibliography"
                id={row.original.id}
                elements={[
                  {
                    label: t("bibliographieDescriptionNumeroBibliographie"),
                    value:
                      row.original.bibliographieDescriptionNumeroBibliographie,
                    type: "text",
                  },
                  {
                    label: t("bibliographieDescriptionResumeLong"),
                    value: row.original.bibliographieDescriptionResumeLong,
                    type: "richtext",
                  },
                  {
                    label: t("attachments"),
                    value: row.original.attachments,
                    type: "image",
                  },
                ]}
              />
              <DocumentInBibliographyDrawer
                key={row.original.id}
                fields={fields}
                queryId={document.id}
                type="document"
                data={{
                  id: "",
                  bibliography: data,
                  document: document,
                  documentsDansBibliographieDescriptionReproduitPage: "",
                  documentsDansBibliographieDescriptionCitePage: "",
                  documentsDansBibliographieDescriptionPhotographe: "",
                  documentsDansBibliographieDescriptionReproductionCouleur:
                    false,
                  documentsDansBibliographieDescriptionNonPagine: false,
                }}
              />
            </div>
          );
        },
      },
    ];
  }, [document, fields, t]);

  const datatableConf = useMemo(
    () =>
      isAddMode ? (
        <DataTable
          columns={columnsBibliographies}
          data={notRelatedBibliographies?.data ?? []}
          pagination={{
            pagination: getPagination,
            setPage: setPagination,
            totalSize: notRelatedBibliographies?.totalSize ?? 0,
          }}
          options={{
            enableColumnResizing: true,
          }}
        />
      ) : (
        <DataTable
          columns={columnsLink}
          data={relatedData ?? []}
          options={{
            enableColumnResizing: true,
          }}
        />
      ),
    [
      columnsBibliographies,
      columnsLink,
      getPagination,
      isAddMode,
      notRelatedBibliographies?.data,
      notRelatedBibliographies?.totalSize,
      relatedData,
    ],
  );

  return datatableConf;
};

// Composant pour Bibliographies
const BibliographyToDocumentLinks = ({
  id,
  relatedData,
  fields,
  bibliography,
  isAddMode,
  columnsLink,
  searchContent,
}: BibliographyToDocumentLinksProps) => {
  const { t } = useTranslation();
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  const { data: countByStatus } = useCountByStatus();
  const totalSize = countByStatus?.published + countByStatus?.draft;
  const { data: notRelatedDocuments } = useGetNotRelatedResource<Document>({
    resultType: "document",
    resourceId: id,
    pagination: getPagination,
    searchQuery: searchContent,
  });

  const columnsDocuments: ColumnDef<Document, any>[] = useMemo(() => {
    return [
      {
        accessorKey: "documentsDescriptionNumeroEnregistrement",
        header: t("tab_documentsDescriptionNumeroEnregistrement"),
        size: 50,
        cell: ({ row }) => {
          return (
            <TextCell
              value={row.original.documentsDescriptionNumeroEnregistrement}
            />
          );
        },
      },
      {
        accessorKey: "documentsDescriptionVignette",
        header: "",
        size: 100,
        cell: ({ row }) => {
          return (
            <ImageCell
              value={""}
              attachments={row.original.attachments}
              domain={"document"}
            />
          );
        },
      },
      {
        accessorKey: "documentsDescriptionTitre",
        header: t("tab_documentsDescriptionTitre"),
        size: 300,
        cell: ({ row }) => {
          return <HtmlCell value={row.original.documentsDescriptionTitre} />;
        },
      },
      {
        accessorKey: "documentsDescriptionType",
        header: t("tab_documentsDescriptionType"),
        size: 100,
        cell: ({ row }) => {
          return <TextCell value={row.original.documentsDescriptionType} />;
        },
      },
      {
        accessorKey: "actions",
        header: "",
        size: 100,
        cell: ({ row }) => {
          const data = row.original as Document;
          if (!bibliography) return null;
          return (
            <div className="flex items-center gap-1 justify-end mr-2">
              <ShowElements
                type="document"
                id={row.original.id}
                elements={[
                  {
                    label: t("documentsDescriptionNumeroEnregistrement"),
                    value:
                      row.original.documentsDescriptionNumeroEnregistrement,
                    type: "text",
                  },
                  {
                    label: t("documentsDescriptionLegendeRedigee"),
                    value: row.original.documentsDescriptionLegendeRedigee,
                    type: "richtext",
                  },
                  {
                    label: t("attachments"),
                    value: row.original.attachments,
                    type: "image",
                  },
                ]}
              />
              <DocumentInBibliographyDrawer
                key={row.original.id}
                fields={fields}
                queryId={bibliography.id}
                type="bibliography"
                data={{
                  id: "",
                  bibliography: bibliography,
                  document: data,
                  documentsDansBibliographieDescriptionReproduitPage: "",
                  documentsDansBibliographieDescriptionCitePage: "",
                  documentsDansBibliographieDescriptionPhotographe: "",
                  documentsDansBibliographieDescriptionReproductionCouleur:
                    false,
                  documentsDansBibliographieDescriptionNonPagine: false,
                }}
              />
            </div>
          );
        },
      },
    ];
  }, [bibliography, fields, t]);

  const datatableConf = useMemo(
    () =>
      isAddMode ? (
        <DataTable
          columns={columnsDocuments}
          data={notRelatedDocuments?.data ?? []}
          pagination={{
            pagination: getPagination,
            setPage: setPagination,
            totalSize: notRelatedDocuments?.totalSize ?? 0,
          }}
          options={{
            enableColumnResizing: true,
          }}
        />
      ) : (
        <DataTable
          columns={columnsLink}
          data={relatedData ?? []}
          pagination={
            isAddMode
              ? {
                  pagination: getPagination,
                  setPage: setPagination,
                  totalSize: totalSize,
                }
              : undefined
          }
          options={{
            enableColumnResizing: true,
          }}
        />
      ),
    [
      columnsDocuments,
      columnsLink,
      getPagination,
      isAddMode,
      notRelatedDocuments?.data,
      notRelatedDocuments?.totalSize,
      relatedData,
      totalSize,
    ],
  );

  return datatableConf;
};

export default DocumentInBibliographyLinks;
